import { Error } from "@/components/admin/error";
import { Notification } from "@/components/admin/notification";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { Skeleton } from "@/components/ui/skeleton";
import { Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { matchPath, Navigate, useLocation } from "react-router";

import { useConfigurationLoader } from "../root/useConfigurationLoader";
import { MobileNavigation } from "./MobileNavigation";
import { useAdminAccess } from "@/providers/firebase/authProvider";
// Same provider as the desktop shell (layout/Layout.tsx), for the same reason: any surface that
// wants the user to pay, upgrade or add credits calls `useMonetization().open(reason)`, and that
// hook throws outside the provider.
//
// WHY IT IS HERE (measured 2026-09-27, canonical vertical viewport 390x844): with the provider
// only in the desktop shell, `/investor-outreach` at phone width rendered this file's
// ErrorBoundary — "Something went wrong" — on the first paint, so phase 4 never reached
// Candidates and every step after it stalled, while the SAME bundle at 1440x900 passed every
// phase. A page is not "fine on mobile" because its route is registered (learning 10); its
// providers must be mounted too.
import { MonetizationProvider } from "@/components/monetization/MonetizationProvider";

// Mobile CRM surfaces still on fakerest demo data — regular users are sent to
// the real entity list instead (D-005, project013_admin_vs_user.md).
const FAKE_MOBILE_ROUTES = ["/", "/contacts", "/companies", "/tasks"];

export const MobileLayout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();
  const showAdmin = useAdminAccess();
  const location = useLocation();

  const isFakeMobileRoute = FAKE_MOBILE_ROUTES.some((path) =>
    path === "/"
      ? matchPath({ path, end: true }, location.pathname)
      : matchPath({ path: `${path}/*` }, location.pathname),
  );
  if (!showAdmin && isFakeMobileRoute) {
    return <Navigate to="/entities" replace />;
  }

  return (
    <>
      {/* Mobile shell top app bar — theme toggle sits here (before user/menu
          controls) like ThemeToggle does in the desktop TopBar. No
          `hidden sm:inline-flex` here: this IS the mobile shell. */}
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-4">
        <div className="flex-1" />
        <ThemeToggle className="" />
      </header>
      <ErrorBoundary FallbackComponent={Error}>
        <MonetizationProvider>
          <Suspense fallback={<Skeleton className="h-12 w-12 rounded-full" />}>
            {children}
          </Suspense>
        </MonetizationProvider>
      </ErrorBoundary>
      {/* Mobile nav entries are demo/fake CRM — admins only until a real mobile build exists */}
      {showAdmin && <MobileNavigation />}
      <Notification mobileOffset={{ bottom: "72px" }} />
    </>
  );
};
